import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { uploadDocumento } from '../../lib/services/documentServices';
import Icon from '../../components/AppIcon';

// Importi navigacionih komponenti
import DashboardSidebar from '../../components/navigation/DashboardSidebar';
import DashboardHeader from '../../components/navigation/DashboardHeader';
import DashboardFooter from '../../components/navigation/DashboardFooter';
import MobileMenuButton from '../../components/navigation/MobileMenuButton';

// Import modala
import EmployeeInfoModal from '../../components/EmployeeInfoModal';
import DeactivateEmployeeModal from '../../components/DeactivateEmployeeModal';
import ActivateEmployeeModal from '../../components/ActivateEmployeeModal';

import QrCodeModal from '../../components/QrCodeModal';
import AttendanceHistory from './AttendanceHistory';

const DettagliDipendente = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [dipendente, setDipendente] = useState(null);
  const [documenti, setDocumenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  
  // State za layout
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const ITEMS_PER_PAGE = 15;

  // NOVO: State za Custom Kategorije
  const [customKategorije, setCustomKategorije] = useState([]);
  const [isNovaKatModalOpen, setIsNovaKatModalOpen] = useState(false);
  const [novaKategorijaNaziv, setNovaKategorijaNaziv] = useState('');
  // NOVO: State za praćenje brisanja (da spriječiš duple klikove)
  const [deletingId, setDeletingId] = useState(null);
  
  // State za modale
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  
  // NOVO: State za Expiry Info Modal
  const [isExpiryModalOpen, setIsExpiryModalOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Filteri state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnno, setFilterAnno] = useState('Tutti');
  const [filterTipo, setFilterTipo] = useState('Tutti');

  // State za formu upload
  const [file, setFile] = useState(null);
  const [tipologia, setTipologia] = useState('Busta Paga');
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [mese, setMese] = useState('Gennaio');
  
  // Polja za dodatne dokumente
  const [nomeCorso, setNomeCorso] = useState('');
  const [dataValidaDal, setDataValidaDal] = useState('');
  const [dataValidaAl, setDataValidaAl] = useState('');
  const [noteAltro, setNoteAltro] = useState('');
  
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const [showDeleted, setShowDeleted] = useState(false);

  // Lokalizovani mjeseci
  const mjeseci = [
    "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
  ];

  useEffect(() => {
    fetchIniziali();
  }, [id]);

  // --- LOG AKTIVNOSTI ZA ADMINA ---
const logActivity = async (doc, actionType) => {
  try {
    // 1. Ko radi akciju (Admin)
    const { data: { user: adminUser } } = await supabase.auth.getUser();
    if (!adminUser) return;

    // 2. Uzmi profil admina da znamo ime i ulogu
    const { data: adminProfil } = await supabase
      .from('profili')
      .select('nome, cognome, ruolo, id_azienda, email')
      .eq('id', adminUser.id)
      .single();

    // 3. Generiši lokalni timestamp
    const tzOffset = (new Date()).getTimezoneOffset() * 60000; 
    const localISOTime = (new Date(Date.now() - tzOffset)).toISOString().slice(0, -1);

    // 4. Ubaci u bazu
    await supabase.from('audit_logs').insert([{
      id_azienda: adminProfil?.id_azienda,
      executor_id: adminUser.id,
      executor_uuid_text: adminUser.id,
      executor_name: `${adminProfil?.nome || ''} ${adminProfil?.cognome || ''}`.trim(),
      executor_email: adminProfil?.email || adminUser.email,
      executor_role: adminProfil?.ruolo || 'Admin',
      target_user_id: id, // 'id' iz useParams (vlasnik dokumenta)
      target_uuid_text: id,
      action: actionType,
      file_name: doc?.nome_file || (typeof doc === 'string' ? doc : 'N/A'),
      file_id: doc?.id || null,
      user_agent: navigator.userAgent,
      ip_address: 'Admin Panel Request',
      created_at: localISOTime 
    }]);
  } catch (err) {
    console.error("Audit Log Error:", err);
  }
};

const fetchIniziali = async (pageNumber = 0) => {
  if (pageNumber === 0) setLoading(true);
  else setLoadingMore(true);


    try {
      // 1. Dohvatanje podataka o radniku
      const { data: radnik, error: radnikErr } = await supabase
        .from('profili')
        .select('*')
        .eq('id', id)
        .single();
      
      if (radnikErr) throw radnikErr;
      setDipendente(radnik);

    // 2. Dohvatanje dokumenata (MODIFIKOVANO: samo is_deleted = false)
    const from = pageNumber * ITEMS_PER_PAGE;
    const to = from + ITEMS_PER_PAGE - 1;

    const { data: docs, error: docsErr } = await supabase
      .from('documenti')
      .select('*')
      .eq('id_utente', id)
      .eq('is_deleted', false) // NOVO: Filtriramo obrisane
      .order('created_at', { ascending: false })
      .range(from, to);
    
    if (docsErr) throw docsErr;

    if (pageNumber === 0) {
      setDocumenti(docs || []);
    } else {
      setDocumenti(prev => [...prev, ...docs]);
    }

    // Provera da li ima još dokumenata
    if (docs.length < ITEMS_PER_PAGE) {
      setHasMore(false);
    } else {
      setHasMore(true);
    }

      // 3. Dohvatanje prilagođenih kategorija za firmu tog radnika
      const { data: kats, error: katsErr } = await supabase
        .from('kategorije_dokumenata')
        .select('*')
        .eq('id_azienda', radnik.id_azienda);
      
      if (!katsErr) {
        setCustomKategorije(kats || []);
      }

    } catch (err) {
      console.error("Errore fetch dati:", err.message);
    } finally {
      setLoading(false);
    }
  };


 const handleDodajKategoriju = async () => {
  const naziv = novaKategorijaNaziv.trim();
  if (!naziv) return;

  const postoji = customKategorije.some(
    (k) => k.naziv.toLowerCase() === naziv.toLowerCase()
  );

  if (postoji) {
    alert(t('categ.error_duplicate', { defaultValue: 'Questa categoria esiste già!' }));
    return;
  }

  try {
    const { data, error } = await supabase
      .from('kategorije_dokumenata')
      .insert([{ 
        naziv: naziv, 
        id_azienda: dipendente.id_azienda 
      }])
      .select();

    if (error) {
      if (error.code === '23505') {
        alert(t('categ.error_duplicate'));
      } else {
        throw error;
      }
      return;
    }

    setCustomKategorije([...customKategorije, data[0]]);
    setTipologia(data[0].naziv);
    setNovaKategorijaNaziv('');
    setIsNovaKatModalOpen(false);
  } catch (err) {
    alert(t('categ.error_generic') + ": " + err.message);
  }
};
  
  const handleDeleteKategorija = async (katId) => {
    if (!window.confirm(t('categ.confirm_delete', { defaultValue: 'Sei sicuro di voler eliminare questa categoria?' }))) return;
    
    setDeletingId(katId);
    try {
      const { error } = await supabase
        .from('kategorije_dokumenata')
        .delete()
        .eq('id', katId);

      if (error) throw error;

      setCustomKategorije(prev => prev.filter(k => k.id !== katId));
      
      if (tipologia === customKategorije.find(k => k.id === katId)?.naziv) {
        setTipologia('Busta Paga');
      }
    } catch (err) {
      alert(t('categ.error_delete', { defaultValue: 'Errore durante l\'eliminazione' }) + ": " + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleLogout = () => {
    navigate('/landing-authentication');
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

 const handleUpload = async (e) => {
  e.preventDefault();
  if (!file) return alert(t('docs.select_file_alert'));

  setUploading(true);
  try {
    const res = await uploadDocumento({
        file,
        idUtente: id,
        idAzienda: dipendente.id_azienda,
        tipologia,
        anno,
        mese: tipologia === 'Busta Paga' ? mese : null,
        nome_corso: tipologia === 'Certificato Corso' ? nomeCorso : null,
        valido_dal: (tipologia === 'Visita Medica' || tipologia === 'Certificato Corso') ? dataValidaDal : null,
        valido_al: (tipologia === 'Visita Medica' || tipologia === 'Certificato Corso') ? dataValidaAl : null,
        note: tipologia === 'Altro' ? noteAltro : null,
        procitano: false 
      });

      if (res.success) {
        await logActivity({ nome_file: file.name }, 'UPLOAD');
        setFile(null);
        setNomeCorso('');
        setDataValidaDal('');
        setDataValidaAl('');
        setNoteAltro('');
        alert(t('docs.upload_success'));
        fetchIniziali();
        
      }
    } catch (err) {
      alert(t('docs.upload_error') + ": " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (docId, path, nomeFile) => {
    try {
      setDownloadingId(docId);
      const { data, error } = await supabase.storage.from('aziende').download(path);
      if (error) throw error;

      await logActivity({ id: docId, nome_file: nomeFile }, 'DOWNLOAD');

      const blobUrl = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', nomeFile);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Errore download:", err.message);
      alert(t('docs.download_error'));
    } finally {
      setDownloadingId(null);
    }
  };

 // --- MODIFIKOVANA DELETE FUNKCIJA (SOFT DELETE SA DELETED_BY) ---
  const handleDeleteDoc = async (docId, path) => {
    if (!window.confirm(t('docs.confirm_delete'))) return;
    
    setDeletingId(docId); 
    try {
      // 1. Ko radi akciju (Admin)
      const { data: { user: adminUser } } = await supabase.auth.getUser();
      if (!adminUser) throw new Error("Utente non autenticato");

      // 2. Pronađi dokument u listi za audit log
      const docToDelete = documenti.find(d => d.id === docId);
      
      // 3. Upis u Audit Log
      await logActivity(docToDelete, 'SOFT DELETE');

      // 4. SOFT DELETE u bazi (update)
      // Dodajemo 'deleted_by' kolonu
      const { error } = await supabase
        .from('documenti')
        .update({ 
          is_deleted: true, 
          deleted_at: new Date().toISOString(),
          deleted_by: adminUser.id // <--- OVO UPISUJE ID ADMINA
        })
        .eq('id', docId);

      if (error) throw error;

      // Osvježavamo listu (uklanja dokument iz vidljivog dijela jer fetchIniziali filtrira is_deleted: false)
      setDocumenti(prev => prev.filter(d => d.id !== docId));
      
      // Opcionalno: fetchIniziali(0); // Ako želiš potpuni refresh sa servera

    } catch (err) {
      console.error("Delete Error:", err);
      alert(t('docs.delete_error') + ": " + err.message);
    } finally {
      setDeletingId(null);
    }
  };
  
  

  const openExpiryInfo = (doc) => {
    setSelectedDoc(doc);
    setIsExpiryModalOpen(true);
  };

  const handleLoadMore = () => {
  const nextPage = page + 1;
  setPage(nextPage);
  fetchIniziali(nextPage);
};


// 1. Izvlači samo godine koje postoje u bazi 'documenti'
const uniqueYears = useMemo(() => {
  const years = documenti.map(d => d.anno.toString());
  return [...new Set(years)].sort((a, b) => b - a);
}, [documenti]);

// 2. Izvlači samo tipologije (kategorije) koje realno postoje u 'documenti'
const realnoPostojeceKategorije = useMemo(() => {
  const kats = documenti.map(d => d.tipologia);
  // Uzima samo unikatne vrijednosti koje su zapravo uploadovane
  return [...new Set(kats)].sort();
}, [documenti]);

// 3. Filtriranje dokumenata
const filteredDocumenti = documenti.filter(doc => {
  const s = searchTerm.toLowerCase();
  const dateStr = new Date(doc.created_at).toLocaleDateString(i18n.language === 'bs' ? 'bs-BA' : i18n.language);
  
  const matchesSearch = 
    doc.nome_file.toLowerCase().includes(s) || 
    doc.tipologia.toLowerCase().includes(s) ||
    (doc.mese && doc.mese.toLowerCase().includes(s)) ||
    doc.anno.toString().includes(s) ||
    dateStr.includes(s);

  const matchesAnno = filterAnno === 'Tutti' || doc.anno.toString() === filterAnno;
  const matchesTipo = filterTipo === 'Tutti' || doc.tipologia === filterTipo;

  return matchesSearch && matchesAnno && matchesTipo;
});

  if (loading) return (
    <div className="p-20 text-center text-primary">
      <div className="animate-spin mb-4"><Icon name="Loader2" size={40} className="mx-auto" /></div>
      <p className="text-muted-foreground text-sm uppercase tracking-widest">{t('common.loading')}</p>
    </div>
  );


return (
    <div className="min-h-screen bg-background text-foreground">
      <MobileMenuButton isOpen={isSidebarOpen} onClick={toggleSidebar} />
      <DashboardSidebar isCollapsed={!isSidebarOpen} onToggle={toggleSidebar} />
      <DashboardHeader onLogout={handleLogout} userName="Admin" />

      <main className="main-content with-sidebar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10 space-y-8">
          
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-medium group"
            >
              <Icon name="ArrowLeft" size={20} className="group-hover:-translate-x-1 transition-transform" /> 
              {t('employee.back_to_list')}
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-2xl border border-border shadow-sm">
                <div className="flex items-center justify-between mb-6">
    <div className="flex items-center gap-4">
  <div className={`w-14 h-14 rounded-full flex items-center justify-center font-bold border text-xl ${
    dipendente?.attivo === false 
      ? "bg-destructive/10 text-destructive border-destructive/20" 
      : "bg-primary/10 text-primary border-primary/20"
  }`}>
    {dipendente?.nome?.[0]}{dipendente?.cognome?.[0]}
  </div>
  <div>
    <div className="flex items-center gap-2">
      <h2 className={`font-bold text-xl ${dipendente?.attivo === false ? "text-destructive line-through opacity-70" : ""}`}>
        {dipendente?.nome} {dipendente?.cognome}
      </h2>
    </div>
    <p className="text-xs text-muted-foreground uppercase font-bold tracking-tighter opacity-70">
      {dipendente?.ruolo}
    </p>
  </div>

</div>
<div className="flex flex-col items-end gap-2">
  <div className="flex gap-2">
    <button 
      onClick={() => setIsQrModalOpen(true)}
      className="p-2 bg-muted hover:bg-primary/10 hover:text-primary rounded-xl border border-border transition-all group"
      title={t('employee.generate_qr')}
    >
      <Icon name="QrCode" size={18} className="group-hover:scale-110 transition-transform" />
    </button>
    
    <button 
      onClick={() => setIsInfoModalOpen(true)}
      className="p-2 bg-muted hover:bg-primary/10 hover:text-primary rounded-xl border border-border transition-all group"
      title={t('employee.info_title')}
    >
      <Icon name="Info" size={18} className="group-hover:scale-110 transition-transform" />
    </button>

    <button 
      onClick={() => dipendente?.attivo === false ? setIsActivateModalOpen(true) : setIsDeactivateModalOpen(true)}
      className={`p-2 rounded-xl border transition-all group ${
        dipendente?.attivo === false 
          ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 hover:bg-emerald-500/20" 
          : "bg-muted hover:bg-destructive/10 hover:text-destructive border-border"
      }`}
      title={dipendente?.attivo === false ? "Aktiviraj Radnika" : "Deaktiviraj Radnika"}
    >
      <Icon 
        name={dipendente?.attivo === false ? "UserCheck" : "UserX"} 
        size={18} 
        className="group-hover:scale-110 transition-transform" 
      />
    </button>
  </div>

 {dipendente?.attivo === false && (
  <div className="animate-in fade-in slide-in-from-top-1">
    <span className="bg-destructive/10 text-destructive text-[9px] px-2 py-1 rounded-lg uppercase font-black tracking-widest border border-destructive/20">
      {t('employee.not_active')}
    </span>
  </div>
  )}
</div>
                </div>

                <form onSubmit={handleUpload} className="space-y-4">
                  <div className="flex items-center gap-2 border-b border-border pb-2 mb-2">
                    <Icon name="PlusCircle" size={14} className="text-primary" />
                    <h3 className="text-[10px] font-bold uppercase text-muted-foreground tracking-widest">{t('docs.new_upload')}</h3>
                  </div>
                  
                  <div className="space-y-1">
                  <div className="flex items-center justify-between">
  <label className="text-[11px] font-bold text-muted-foreground ml-1 uppercase">{t('docs.type')}</label>
<button 
      type="button" 
      onClick={() => setIsNovaKatModalOpen(true)}
      className="text-[10px] text-primary font-black uppercase tracking-wider hover:opacity-70 transition-all flex items-center gap-1"
    >
      <Icon name="Plus" size={10} />
      {t('categ.title2', { defaultValue: 'Nuova Categoria' })}
    </button>
</div>
                    <select 
                      className="w-full p-2.5 bg-background border border-border rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/20 font-bold" 
                      value={tipologia} 
                      onChange={(e) => setTipologia(e.target.value)}
                    >
                      <option value="Busta Paga">{t('docs.types.payslip')}</option>
                      <option value="CU">{t('docs.types.cu')}</option>
                      <option value="Contratto">{t('docs.types.contract')}</option>
                      <option value="Visita Medica">{t('docs.types.medical')}</option>
                      <option value="Certificato Corso">{t('docs.types.course')}</option>
                      <option value="Altro">{t('docs.types.other')}</option>

                      {customKategorije.map(kat => (
    <option key={kat.id} value={kat.naziv}>{kat.naziv}</option>
  ))}
                    </select>
                  </div>

                  {tipologia === 'Certificato Corso' && (
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-muted-foreground ml-1 uppercase">{t('docs.course_name')}</label>
                      <input 
                        type="text" 
                        placeholder={t('docs.course_placeholder')}
                        className="w-full p-2.5 bg-background border border-border rounded-xl text-sm outline-none font-medium" 
                        value={nomeCorso} 
                        onChange={(e) => setNomeCorso(e.target.value)} 
                      />
                    </div>
                  )}

                  {(tipologia === 'Visita Medica' || tipologia === 'Certificato Corso') && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-muted-foreground ml-1 uppercase">{t('docs.valid_from')}</label>
                        <input type="date" className="w-full p-2.5 bg-background border border-border rounded-xl text-sm outline-none font-medium" value={dataValidaDal} onChange={(e) => setDataValidaDal(e.target.value)} />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-muted-foreground ml-1 uppercase">{t('docs.valid_to')}</label>
                        <input type="date" className="w-full p-2.5 bg-background border border-border rounded-xl text-sm outline-none font-medium" value={dataValidaAl} onChange={(e) => setDataValidaAl(e.target.value)} />
                      </div>
                    </div>
                  )}

                  {tipologia === 'Altro' && (
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-muted-foreground ml-1 uppercase">{t('docs.notes')}</label>
                      <textarea 
                        className="w-full p-2.5 bg-background border border-border rounded-xl text-sm outline-none min-h-[80px]" 
                        placeholder={t('docs.notes_placeholder')}
                        value={noteAltro} 
                        onChange={(e) => setNoteAltro(e.target.value)} 
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-muted-foreground ml-1 uppercase">{t('docs.year')}</label>
                      <input type="number" className="w-full p-2.5 bg-background border border-border rounded-xl text-sm outline-none font-bold" value={anno} onChange={(e) => setAnno(e.target.value)} />
                    </div>
                    {tipologia === 'Busta Paga' && (
                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-muted-foreground ml-1 uppercase">{t('docs.month')}</label>
                        <select className="w-full p-2.5 bg-background border border-border rounded-xl text-sm outline-none font-bold" value={mese} onChange={(e) => setMese(e.target.value)}>
                          {mjeseci.map(m => <option key={m} value={m}>{t(`months.${m}`)}</option>)}
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <input type="file" id="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
                    <label htmlFor="file" className="flex flex-col items-center justify-center border-2 border-dashed border-border p-6 rounded-2xl cursor-pointer hover:border-primary/40 hover:bg-primary/[0.02] transition-all">
                      <Icon name="UploadCloud" className="text-muted-foreground mb-2" size={32} />
                      <span className="text-xs font-bold text-muted-foreground text-center px-2">
                        {file ? file.name : t('docs.select_pdf_img')}
                      </span>
                    </label>
                  </div>

                  <button 
                    disabled={uploading || !file} 
                    className="w-full bg-primary text-white py-3 rounded-xl font-bold hover:opacity-90 transition-all disabled:opacity-40 shadow-lg shadow-primary/20"
                  >
                    {uploading ? t('docs.uploading') : t('docs.upload_btn')}
                  </button>
                </form>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
              <div className="bg-card p-4 rounded-2xl border border-border shadow-sm flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-[2] w-full">
                  <label className="text-[10px] font-black uppercase text-muted-foreground mb-1.5 block ml-1 tracking-widest">{t('docs.filter_archive')}</label>
                  <div className="relative">
                    <Icon name="Search" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
                    <input 
                      type="text" 
                      placeholder={t('docs.search_placeholder')} 
                      className="w-full pl-11 pr-4 py-2.5 bg-muted/30 border border-border rounded-xl text-sm outline-none focus:bg-background transition-all font-medium"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
             <div className="flex-1 w-full grid grid-cols-2 gap-2">
   <div>
      <label className="text-[10px] font-black uppercase text-muted-foreground mb-1.5 block ml-1 tracking-widest">{t('docs.year')}</label>
      <select 
        className="w-full p-2.5 bg-muted/30 border border-border rounded-xl text-sm outline-none font-bold" 
        value={filterAnno} 
        onChange={(e) => setFilterAnno(e.target.value)}
      >
        <option value="Tutti">{t('common.all')}</option>
        {uniqueYears.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
   </div>
 <div>
  <label className="text-[10px] font-black uppercase text-muted-foreground mb-1.5 block ml-1 tracking-widest">
    {t('docs.type')}
  </label>
  <select 
    className="w-full p-2.5 bg-muted/30 border border-border rounded-xl text-sm outline-none font-bold" 
    value={filterTipo} 
    onChange={(e) => setFilterTipo(e.target.value)}
  >
    <option value="Tutti">{t('common.all')}</option>
    {realnoPostojeceKategorije.map((kat) => (
      <option key={kat} value={kat}>
        {/* Provjeravamo da li imamo prevod za taj tip, ako ne, ispisujemo sirovo iz baze */}
        {['Busta Paga', 'CU', 'Contratto', 'Visita Medica', 'Certificato Corso', 'Altro'].includes(kat) 
          ? t(`docs.types.${kat.toLowerCase().replace(' ', '_')}`, { defaultValue: kat }) 
          : kat}
      </option>
    ))}
  </select>
</div>
</div>
              </div>

              {/* TABELA SA SCROLLBAR-OM I PRIKAŽI VIŠE */}
              <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex flex-col">
                <div className="p-4 border-b border-border bg-muted/10">
                  <h3 className="font-bold flex items-center gap-2 uppercase text-xs tracking-widest text-muted-foreground">
                    <Icon name="FileText" size={16} className="text-primary" /> {t('docs.list_title')}
                  </h3>
                </div>
                
                {/* Scroll kontejner */}
                <div className="overflow-x-auto overflow-y-auto max-h-[600px] custom-scrollbar">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead className="bg-muted/30 text-muted-foreground border-b border-border sticky top-0 z-10 backdrop-blur-sm">
                      <tr>
                        <th className="p-4 font-bold uppercase text-[10px] tracking-widest">{t('docs.col_name')}</th>
                        <th className="p-4 font-bold uppercase text-[10px] tracking-widest">{t('docs.col_type')}</th>
                        <th className="p-4 font-bold uppercase text-[10px] tracking-widest">{t('docs.col_period')}</th>
                        <th className="p-4 font-bold uppercase text-[10px] tracking-widest">{t('docs.col_date')}</th>
                        <th className="p-4 text-right font-bold uppercase text-[10px] tracking-widest">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredDocumenti.length > 0 ? filteredDocumenti.map(doc => (
                        <tr 
                          key={doc.id} 
                          className={`transition-colors group ${!doc.procitano ? 'bg-primary/5 font-black' : 'hover:bg-muted/10'}`}
                        >
                          <td className="p-4 text-foreground">
                            <div className="flex items-center gap-2">
                              <Icon 
                                name={!doc.procitano ? "EyeOff" : "Eye"} 
                                size={14} 
                                className={!doc.procitano ? "text-primary" : "text-emerald-500"} 
                              />
                              <div className="flex flex-col">
                                <span>{doc.nome_file}</span>
                                <span className={`text-[9px] uppercase font-black tracking-widest ${!doc.procitano ? 'text-primary animate-pulse' : 'text-emerald-500 opacity-70'}`}>
                                  {!doc.procitano 
                                    ? t('common.unread', { defaultValue: 'NEPROČITANO' }) 
                                    : t('common.read', { defaultValue: 'PROČITANO' })}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-[9px] font-black border uppercase tracking-tighter ${
                              doc.tipologia === 'Busta Paga' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' : 
                              doc.tipologia === 'Visita Medica' ? 'bg-red-500/10 text-red-500 border-red-500/20' :
                              doc.tipologia === 'Certificato Corso' ? 'bg-purple-500/10 text-purple-500 border-purple-500/20' :
                              'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                            }`}>
                              {doc.tipologia === 'Busta Paga' ? t('docs.types.payslip') : 
                               doc.tipologia === 'CU' ? t('docs.types.cu') : 
                               doc.tipologia === 'Contratto' ? t('docs.types.contract') : 
                               doc.tipologia === 'Visita Medica' ? t('docs.types.medical') : 
                               doc.tipologia === 'Certificato Corso' ? t('docs.types.course') : doc.tipologia}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col">
                              {doc.valido_al ? (
                                <span className="text-[10px] font-bold text-orange-600 uppercase">
                                  {t('docs.expires')}: {new Date(doc.valido_al).toLocaleDateString(i18n.language === 'bs' ? 'bs-BA' : i18n.language)}
                                </span>
                              ) : (
                                <span className="font-bold">
                                  {doc.mese ? t(`months.${doc.mese}`) : t('common.annual')}
                                </span>
                              )}
                              <span className="text-[10px] text-muted-foreground font-bold">{doc.anno}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="text-xs font-medium">
                              <span>{new Date(doc.created_at).toLocaleDateString(i18n.language === 'bs' ? 'bs-BA' : i18n.language)}</span>
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex justify-end gap-2">
                              <button 
                                className="p-2.5 text-muted-foreground hover:bg-primary/10 hover:text-primary border border-border rounded-xl transition-all shadow-sm"
                                onClick={() => openExpiryInfo(doc)}
                                title="Info"
                              >
                                <Icon name="Info" size={16} />
                              </button>
                              
                              <button 
                                disabled={downloadingId === doc.id}
                                className={`p-2.5 border border-border rounded-xl transition-all shadow-sm ${
                                  downloadingId === doc.id ? 'bg-muted' : 'text-muted-foreground hover:bg-primary hover:text-white hover:border-primary'
                                }`} 
                                onClick={() => handleDownload(doc.id, doc.url_path, doc.nome_file)}
                              >
                                <Icon 
                                  name={downloadingId === doc.id ? "Loader2" : "Download"} 
                                  size={16} 
                                  className={downloadingId === doc.id ? "animate-spin" : ""}
                                />
                              </button>
                              <button 
                                className="p-2.5 text-muted-foreground hover:bg-destructive hover:text-white hover:border-destructive border border-border rounded-xl transition-all shadow-sm" 
                                onClick={() => handleDeleteDoc(doc.id, doc.url_path)}
                              >
                                <Icon name="Trash2" size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="5" className="p-10 text-center text-muted-foreground italic">
                            {t('docs.no_docs_found')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Dugme Prikaži Više (Load More) na dnu kartice */}
                {hasMore && (
                  <div className="p-4 border-t border-border bg-muted/5 flex justify-center">
                    <button 
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="flex items-center gap-2 px-6 py-2 rounded-xl bg-background border border-border text-[10px] font-black uppercase tracking-widest hover:border-primary hover:text-primary transition-all disabled:opacity-50 shadow-sm"
                    >
                      {loadingMore ? (
                        <Icon name="Loader2" size={14} className="animate-spin" />
                      ) : (
                        <>
                          <Icon name="ChevronDown" size={14} />
                          {t('userLog.loadMore', { defaultValue: 'Prikaži više' })}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
            <AttendanceHistory userId={id} />
        </div>
      </main>
      <DashboardFooter />
  

      {/* MODALI POSTOJEĆI */}
      <EmployeeInfoModal 
        isOpen={isInfoModalOpen} 
        onClose={() => setIsInfoModalOpen(false)} 
        data={dipendente} 
        onUpdateSuccess={fetchIniziali}
      />

      <QrCodeModal 
        isOpen={isQrModalOpen} 
        onClose={() => setIsQrModalOpen(false)} 
        data={dipendente} 
      />

<DeactivateEmployeeModal 
  isOpen={isDeactivateModalOpen}
  onClose={() => setIsDeactivateModalOpen(false)}
  employee={dipendente} 
  onDeactivateSuccess={() => {
    // Umjesto navigate, samo osvježavamo podatke na istoj stranici
    fetchIniziali();
  }}
/>

<ActivateEmployeeModal 
  isOpen={isActivateModalOpen}
  onClose={() => setIsActivateModalOpen(false)}
  employee={dipendente} 
  onActivateSuccess={() => {
    fetchIniziali(); // Osvježi podatke da ime više ne bude crveno
  }}
/>

      {/* NOVO: MODAL ZA INFO O ISTEKU */}
      {isExpiryModalOpen && selectedDoc && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-card w-full max-w-sm rounded-3xl border border-border shadow-2xl p-6 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-sm font-black uppercase tracking-widest text-primary flex items-center gap-2">
                <Icon name="Info" size={18} /> {t('employee.info_title')}
              </h3>
              <button onClick={() => setIsExpiryModalOpen(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-2xl border border-border">
                <p className="text-[10px] font-black uppercase text-muted-foreground mb-1">{t('docs.col_name')}</p>
                <p className="text-sm font-bold truncate">{selectedDoc.nome_file}</p>
              </div>

              {selectedDoc.nome_corso && (
                <div className="p-4 bg-muted/30 rounded-2xl border border-border">
                  <p className="text-[10px] font-black uppercase text-muted-foreground mb-1">{t('docs.course_name')}</p>
                  <p className="text-sm font-bold text-primary">{selectedDoc.nome_corso}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-emerald-500/5 rounded-2xl border border-emerald-500/10">
                  <p className="text-[10px] font-black uppercase text-emerald-600 mb-1">{t('docs.valid_from')}</p>
                  <p className="text-sm font-black">
                    {selectedDoc.valido_dal ? new Date(selectedDoc.valido_dal).toLocaleDateString(i18n.language === 'bs' ? 'bs-BA' : i18n.language) : "-"}
                  </p>
                </div>
                <div className="p-4 bg-destructive/5 rounded-2xl border border-destructive/10">
                  <p className="text-[10px] font-black uppercase text-destructive mb-1">{t('docs.valid_to')}</p>
                  <p className="text-sm font-black">
                    {selectedDoc.valido_al ? new Date(selectedDoc.valido_al).toLocaleDateString(i18n.language === 'bs' ? 'bs-BA' : i18n.language) : "-"}
                  </p>
                </div>
              </div>

              {selectedDoc.note && (
                <div className="p-4 bg-muted/30 rounded-2xl border border-border">
                  <p className="text-[10px] font-black uppercase text-muted-foreground mb-1">{t('docs.notes')}</p>
                  <p className="text-xs font-medium italic text-muted-foreground leading-relaxed">{selectedDoc.note}</p>
                </div>
              )}
            </div>

            <button 
              onClick={() => setIsExpiryModalOpen(false)}
              className="w-full mt-6 bg-primary text-white py-3 rounded-xl font-bold hover:opacity-90 transition-all shadow-lg shadow-primary/20"
            >
              OK
            </button>
          </div>
        </div>
      )}

{/* MODAL ZA UPRAVLJANJE KATEGORIJAMA - SA LISTOM */}
{isNovaKatModalOpen && (
  <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
    <div className="bg-card w-full max-w-md rounded-3xl border border-border shadow-2xl overflow-hidden animate-in zoom-in duration-200">
      
      {/* Modal Header */}
      <div className="p-6 border-b border-border flex justify-between items-center bg-muted/20">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <Icon name="Settings2" size={20} />
          </div>
          <h3 className="font-bold text-lg text-foreground">
            {t('categ.manager_title', { defaultValue: 'Gestisci Categorie' })}
          </h3>
        </div>
        <button 
          onClick={() => { setNovaKategorijaNaziv(''); setIsNovaKatModalOpen(false); }} 
          className="p-2 hover:bg-muted rounded-full transition-colors text-muted-foreground"
        >
          <Icon name="X" size={20} />
        </button>
      </div>

      {/* Modal Body */}
      <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
        
        {/* Form za unos */}
        <div className="space-y-3">
          <div className="flex flex-col space-y-1">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Icon name="Plus" size={12} className="opacity-70" />
              <span className="text-[10px] font-black uppercase tracking-widest">
                {t('categ.add_new', { defaultValue: 'Aggiungi Nuova' })}
              </span>
            </div>
            <div className="flex gap-2">
              <input
                autoFocus
                type="text"
                placeholder={t('categ.placeholder', { defaultValue: 'Nome categoria...' })}
                className="flex-1 bg-background border border-primary/30 rounded-xl px-4 py-2.5 text-sm font-semibold text-foreground outline-none focus:ring-4 focus:ring-primary/10 transition-all"
                value={novaKategorijaNaziv}
                onChange={(e) => setNovaKategorijaNaziv(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleDodajKategoriju(); }}
              />
              <button 
                onClick={handleDodajKategoriju}
                disabled={!novaKategorijaNaziv.trim()}
                className="bg-primary text-white p-2.5 rounded-xl hover:opacity-90 disabled:opacity-40 transition-all shadow-lg shadow-primary/20"
              >
                <Icon name="Check" size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Lista postojećih kategorija */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="List" size={12} className="opacity-70" />
            <span className="text-[10px] font-black uppercase tracking-widest">
              {t('categ.your_list', { defaultValue: 'Le tue categorie' })}
            </span>
          </div>

          <div className="grid gap-2">
            {customKategorije.length > 0 ? (
              customKategorije.map((kat) => (
                <div 
                  key={kat.id} 
                  className="flex items-center justify-between p-3 bg-muted/30 border border-border rounded-2xl group hover:border-primary/30 transition-all"
                >
                  <span className="text-sm font-bold text-foreground">{kat.naziv}</span>
                  <button 
                    onClick={() => handleDeleteKategorija(kat.id)}
                    className="p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive rounded-lg transition-all"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-xs text-muted-foreground italic text-center py-4 bg-muted/10 rounded-2xl border border-dashed border-border">
                {t('categ.no_custom', { defaultValue: 'Nessuna categoria personalizzata' })}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Modal Footer */}
      <div className="p-4 bg-muted/10 border-t border-border">
        <button 
          onClick={() => { setNovaKategorijaNaziv(''); setIsNovaKatModalOpen(false); }}
          className="w-full px-6 py-2.5 bg-muted text-foreground rounded-xl font-bold text-sm hover:bg-muted/80 transition-all border border-border uppercase tracking-widest"
        >
          {t('common.close', { defaultValue: 'Chiudi' })}
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default DettagliDipendente;